import { useMsal } from '@azure/msal-react'
import ToggleOffIcon from '@mui/icons-material/ToggleOff'
import ToggleOnIcon from '@mui/icons-material/ToggleOn'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import { deepPurple } from '@mui/material/colors'
import Divider from '@mui/material/Divider'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import { MouseEvent, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from 'src/hooks/useAppDispatch'
import { SearchAndCompareEnum } from 'src/lib/constants'
import { getEnvVar } from 'src/lib/env'
import { cancelAllCommunityPlansRequests } from 'src/lib/requestCancellation'
import {
  getDashboardData,
  getMultiFamilyDashboardData,
  removeItemFromRecentDraftPlans,
} from 'src/redux/slice/DashboardSlice'
import { RootState } from 'src/redux/stores/store'

import { MODAL_TYPE } from '../../constants/modal.constants'
import { getReleaseNotesAction } from '../../redux/slice/GetReleaseNotesSlice'
import { setModal } from '../../redux/slice/modalSlice'
import SupportActions from './SupportActions'

export const UserAvatarAndSettingsMenu = () => {
  const {
    isCorporateCreateUser,
    isCorporateViewUser,
    isDivisionCreateUser,
    isDivisionViewUser,
    userInfo,
  } = useSelector((state: RootState) => state.Auth)
  const isViewOnlyUser = isCorporateViewUser || isDivisionViewUser
  const { accounts, instance } = useMsal()

  const [userSettingsMenu, setUserSettingsMenu] = useState<
    (EventTarget & Element) | null
  >(null)

  const setItemToLocalStorage = (key: string, value: string): void => {
    localStorage.setItem(key, value)

    // dispatch custom event to notify other components that may subscribe to this change
    const customEvent = new CustomEvent(SearchAndCompareEnum.EVENT_NAME, {
      detail: { key, value },
    })
    window.dispatchEvent(customEvent)
  }

  const getItemFromLocalStorage = (key: string) => {
    const storedValue = localStorage.getItem(key)
    return storedValue
  }

  const setInitialSearchAndCompareSetting = () => {
    if (isCorporateCreateUser || isDivisionCreateUser) {
      const localStorageValue = getItemFromLocalStorage(
        SearchAndCompareEnum.KEY
      )
      if (localStorageValue === SearchAndCompareEnum.ENABLED) {
        return true
      }
    }

    return false
  }

  const [searchAndCompareMode, setSearchAndCompareMode] = useState<boolean>(
    setInitialSearchAndCompareSetting
  )

  // initialize and sync localStorage state
  useEffect(() => {
    if (isViewOnlyUser) return

    const updateSearchAndCompareMode = () => {
      if (isCorporateCreateUser || isDivisionCreateUser) {
        const localStorageValue = localStorage.getItem(SearchAndCompareEnum.KEY)
        setSearchAndCompareMode(
          localStorageValue === SearchAndCompareEnum.ENABLED
        )
      } else {
        setSearchAndCompareMode(false)
      }
    }

    updateSearchAndCompareMode()

    // listen for localStorage changes from other tabs
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === SearchAndCompareEnum.KEY) {
        updateSearchAndCompareMode()
      }
    }

    // listen for custom localStorage changes in same tab
    const handleCustomStorageChange = (event: CustomEvent) => {
      if (event.detail?.key === SearchAndCompareEnum.KEY) {
        updateSearchAndCompareMode()
      }
    }

    window.addEventListener(
      SearchAndCompareEnum.LOCAL_STORAGE_NAME,
      handleStorageChange
    )
    window.addEventListener(
      SearchAndCompareEnum.EVENT_NAME,
      handleCustomStorageChange as EventListener
    )

    return () => {
      window.removeEventListener(
        SearchAndCompareEnum.LOCAL_STORAGE_NAME,
        handleStorageChange
      )
      window.removeEventListener(
        SearchAndCompareEnum.EVENT_NAME,
        handleCustomStorageChange as EventListener
      )
    }
  }, [isCorporateCreateUser, isDivisionCreateUser])

  const enableSearchAndCompareMode = () => {
    // Leaving Create mode: abort any in-flight all-community-plans prefetch
    // so we don't keep fetching data the user no longer needs.
    cancelAllCommunityPlansRequests()
    setSearchAndCompareMode(true)
    setItemToLocalStorage(
      SearchAndCompareEnum.KEY,
      SearchAndCompareEnum.ENABLED
    )
  }

  // Note: only corporate create and division create users can toggle this setting
  const onSearchAndCompareModeToggle = () => {
    const localStorageValue = getItemFromLocalStorage(SearchAndCompareEnum.KEY)
    if (!localStorageValue) {
      enableSearchAndCompareMode()
    } else if (localStorageValue === SearchAndCompareEnum.ENABLED) {
      setSearchAndCompareMode(false)
      setItemToLocalStorage(
        SearchAndCompareEnum.KEY,
        SearchAndCompareEnum.DISABLED
      )
    } else if (localStorageValue === SearchAndCompareEnum.DISABLED) {
      enableSearchAndCompareMode()
    }
  }

  const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) =>
    setUserSettingsMenu(event.currentTarget)
  const handleCloseUserMenu = () => setUserSettingsMenu(null)
  const onLogoutClick = async () => {
    if (instance) {
      try {
        await instance.logoutPopup({ mainWindowRedirectUri: '/logout' })
      } catch (error) {
        return error
      }
    } else {
      return 'MSAL instance not available'
    }
  }
  const [initial, setInitial] = useState('')
  const settings = [
    {
      action: onLogoutClick,
      item: 'Log Out',
    },
  ]

  const appDispatch = useAppDispatch()
  const { data: releaseNotesData } = useSelector(
    (state: RootState) => state.ReleaseNotes
  )

  const doReleaseNotesExist = !!releaseNotesData?.list.length
  const latestVersion = releaseNotesData?.latestVersion

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { selectedCommunityPlanId } = useSelector(
    (state: RootState) => state.Dashboard
  )

  const handleModalChange = (property: MODAL_TYPE, value: object) => {
    appDispatch(setModal({ property, value }))
  }

  const getInitials = (familyName: string, givenName: string) => {
    const initials = []
    if (familyName) {
      initials.push(familyName[0].toUpperCase())
    }
    if (givenName) {
      initials.push(givenName[0].toUpperCase())
    }
    return initials.join('')
  }

  useEffect(() => {
    const user = accounts[0]
    if (user) {
      const familyName = user.name?.split(' ')[0] || ''
      const givenName = user.name?.split(' ')[1] || ''
      setInitial(getInitials(familyName, givenName))
    }
  }, [accounts])

  useEffect(() => {
    if (!doReleaseNotesExist) appDispatch(getReleaseNotesAction())
  }, [appDispatch, doReleaseNotesExist])

  useEffect(() => {
    const fetchDashboardData = async () => {
      await appDispatch(getDashboardData())
      await appDispatch(getMultiFamilyDashboardData())
      // Temporary change
      if (selectedCommunityPlanId) {
        appDispatch(removeItemFromRecentDraftPlans(selectedCommunityPlanId))
      }
    }
    fetchDashboardData()
  }, [userInfo, selectedCommunityPlanId, appDispatch])

  return (
    <Box sx={{ alignItems: 'center', display: 'flex', flexGrow: 0, gap: 1.5 }}>
      <SupportActions variant="header" />
      <Divider
        orientation="vertical"
        flexItem
        sx={{ alignSelf: 'center', height: 26, mx: 0.5 }}
      />
      {/* Search & Compare Mode Toggle - only show for corporate create and division create users */}
      {(isCorporateCreateUser || isDivisionCreateUser) && (
        <Box
          sx={{
            alignItems: 'center',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'row',
            gap: 1,
          }}
          onClick={onSearchAndCompareModeToggle}
        >
          <Typography
            variant="body2"
            sx={{
              color: (theme) => theme.lennarDesignSystem.neutrals.grey8,
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
          >
            {SearchAndCompareEnum.SETTINGS_LABEL}
          </Typography>

          {searchAndCompareMode ? (
            <ToggleOnIcon
              sx={{
                color: (theme) =>
                  theme.lennarDesignSystem.lennarBlues.lennarBlue,
                fontSize: '1.7rem',
              }}
            />
          ) : (
            <ToggleOffIcon
              sx={{
                color: (theme) => theme.lennarDesignSystem.neutrals.grey6,
                fontSize: '1.7rem',
              }}
            />
          )}
        </Box>
      )}

      <Avatar
        sx={{
          bgcolor: deepPurple[500],
          cursor: 'pointer',
          height: 38,
          ml: 1,
          width: 38,
        }}
        onClick={handleOpenUserMenu}
      >
        {initial}
      </Avatar>
      <Menu
        sx={{ mt: '45px', width: '18em' }}
        id="menu-app-bar"
        anchorEl={userSettingsMenu}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
        keepMounted
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
        open={Boolean(userSettingsMenu)}
        onClose={handleCloseUserMenu}
      >
        {latestVersion && (
          <MenuItem
            onClick={() =>
              handleModalChange(MODAL_TYPE.RELEASE_NOTES_MODAL, {})
            }
          >
            <Typography textAlign="center">
              {getEnvVar('REACT_APP_NODE_ENV')?.toUpperCase()}-Release/
              {latestVersion ?? ''}
            </Typography>
          </MenuItem>
        )}
        {settings.map((setting) => (
          <MenuItem key={setting.item} onClick={setting.action}>
            <Typography textAlign="center">{setting.item}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}
